import { useState, useEffect, useRef } from "react";
import { transfermarktService, searchService } from "../../services/api";
import { getPositionDisplay } from "../../utils/positionFormatter";

const useTransferSection = (sortType, countries) => {
  // Section state
  const [transfers, setTransfers] = useState([]);
  const [isLoadingTransfers, setIsLoadingTransfers] = useState(true);
  const [transfersError, setTransfersError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    competition_id: "",
    position: "",
    gender: "male",
    level: "pro",
    country: "", // Player nationality
    divisionCountry: "", // Division country for loading leagues
    type: "",
    date_from: "",
    date_to: "",
  });

  // Search states for dropdowns
  const [competitionSearch, setCompetitionSearch] = useState("");
  const [showCompetitionDropdown, setShowCompetitionDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [divisionCountrySearch, setDivisionCountrySearch] = useState("");
  const [showDivisionCountryDropdown, setShowDivisionCountryDropdown] = useState(false);

  // Filter options
  const [competitions, setCompetitions] = useState([]);
  const [filteredCompetitions, setFilteredCompetitions] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState(countries || []);
  const [filteredDivisionCountries, setFilteredDivisionCountries] = useState(countries || []);

  // Pagination
  const TRANSFERS_PER_PAGE = 10;
  const [cursorStack, setCursorStack] = useState([null]);
  const [nextCursor, setNextCursor] = useState(null);
  const [nextPageNumber, setNextPageNumber] = useState(null);
  const [isUsingCursorPagination, setIsUsingCursorPagination] = useState(false);

  // Abort controller
  const controllerRef = useRef(null);

  // Update filtered countries when countries prop changes
  useEffect(() => {
    setFilteredCountries(countries || []);
    setFilteredDivisionCountries(countries || []);
  }, [countries]);

  // Filter countries based on search
  useEffect(() => {
    if (countrySearch && countries) {
      const filtered = countries.filter((country) =>
        country.name.toLowerCase().includes(countrySearch.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries || []);
    }
  }, [countrySearch, countries]);

  // Filter division countries based on search
  useEffect(() => {
    if (divisionCountrySearch && countries) {
      const filtered = countries.filter((country) =>
        country.name.toLowerCase().includes(divisionCountrySearch.toLowerCase())
      );
      setFilteredDivisionCountries(filtered);
    } else {
      setFilteredDivisionCountries(countries || []);
    }
  }, [divisionCountrySearch, countries]);

  // Filter competitions based on search
  useEffect(() => {
    if (competitionSearch) {
      const filtered = competitions.filter((comp) =>
        (comp.title || comp.name)
          .toLowerCase()
          .includes(competitionSearch.toLowerCase())
      );
      setFilteredCompetitions(filtered);
    } else {
      setFilteredCompetitions(competitions);
    }
  }, [competitionSearch, competitions]);

  // Load competitions when division country is selected
  useEffect(() => {
    const loadCompetitions = async () => {
      if (!filters.divisionCountry) {
        setCompetitions([]);
        setFilteredCompetitions([]);
        return;
      }

      try {
        const competitionsRes = await searchService.getLeagues(filters.divisionCountry);
        if (competitionsRes.success) {
          setCompetitions(competitionsRes.data);
          setFilteredCompetitions(competitionsRes.data);
        }
      } catch (error) {
        console.error("Error loading competitions for country:", error);
      }
    };

    loadCompetitions();
  }, [filters.divisionCountry]);

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return undefined;
    const birth = new Date(dob);
    if (Number.isNaN(birth.getTime())) return undefined;
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Fetch transfers data
  const fetchTransfers = async (cursorArg = null, overrideFilters = null, pageOverride = null) => {
    const activeFilters = overrideFilters || filters;
    const pageToUse = pageOverride !== null ? pageOverride : currentPage;

    // Cancel previous request
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;

    setIsLoadingTransfers(true);
    setTransfersError(null);

    try {
      const params = {
        per_page: TRANSFERS_PER_PAGE,
        sort: sortType, // 'top' or 'recent'
      };

      if (cursorArg) {
        params.cursor = cursorArg;
      } else {
        params.page = pageToUse;
      }

      // Add filter parameters
      if (activeFilters.gender) params.gender = activeFilters.gender;
      if (activeFilters.position) params.position = activeFilters.position;
      if (activeFilters.country) params.country = activeFilters.country;
      if (activeFilters.competition_id) params.competition_id = activeFilters.competition_id;
      if (activeFilters.level) params.level = activeFilters.level;
      if (activeFilters.type) params.type = activeFilters.type;
      if (activeFilters.date_from) params.date_from = activeFilters.date_from;
      if (activeFilters.date_to) params.date_to = activeFilters.date_to;

      const response = await transfermarktService.getTransfers(params);

      if (response.success) {
        const mapped = (response.results || response.data || []).map((item) => {
          const playerInfo = item?.player || {};
          const playerName = playerInfo.full_name || playerInfo.name || playerInfo.short_name || "-";
          const playerImage = playerInfo.image_url;
          const positionName = playerInfo.position || "-";
          const positionDisplay = getPositionDisplay(playerInfo, positionName);
          const detailedPositions =
            playerInfo.tp_detailed_position ||
            playerInfo.tpDetailedPosition ||
            playerInfo.tpDetailedPositions ||
            null;

          const normalized = {
            id: item?.transfer_id || item?.id,
            isLoan: item?.type === "loan",
            transferTypeName: item?.type || "-",
            player: {
              id: playerInfo.wyscout_id,
              name: playerName,
              image: playerImage,
              age: calculateAge(playerInfo.date_of_birth),
              position: positionName,
              positionDisplay,
              tp_detailed_position: detailedPositions,
              tpDetailedPosition: detailedPositions,
              tpDetailedPositions: detailedPositions,
              date_of_birth: playerInfo.date_of_birth,
            },
            fromClub: {
              id: item?.from_team?.id || null,
              name: item?.from_team?.name || "Free Agent",
              image: item?.from_team?.image_url,
            },
            toClub: {
              id: item?.to_team?.id || null,
              name: item?.to_team?.name || "Free Agent",
              image: item?.to_team?.image_url,
            },
          };

          if (item?.fee_amount != null && item?.fee_amount !== "") {
            normalized.transferFee = {
              value: item.fee_amount,
              currency: item.fee_currency || "EUR",
            };
          }

          return normalized;
        });

        setTransfers(mapped);
        setHasMore(!!response.pagination?.has_more || !!response.pagination?.has_next);
        // Support both cursor-based and page-based pagination
        const apiNextCursor = response.pagination?.next_cursor;
        const apiNextPage = response.pagination?.next_page_number;
        
        if (apiNextCursor) {
          // Using cursor-based pagination
          setIsUsingCursorPagination(true);
          setNextCursor(apiNextCursor);
          setNextPageNumber(null);
        } else if (apiNextPage) {
          // Using page-based pagination
          setIsUsingCursorPagination(false);
          setNextCursor(null);
          setNextPageNumber(apiNextPage);
        } else {
          // No more pages
          setIsUsingCursorPagination(false);
          setNextCursor(null);
          setNextPageNumber(null);
        }
        
        // Sync current page with API response if using page-based pagination
        if (!apiNextCursor && response.pagination?.current_page) {
          setCurrentPage(response.pagination.current_page);
        }
      } else {
        setTransfersError(response.error);
      }
    } catch (error) {
      if (error?.name === "CanceledError") return;
      setTransfersError("Failed to load transfers");
    } finally {
      if (!controllerRef.current?.signal.aborted) {
        setIsLoadingTransfers(false);
      }
    }
  };

  // Filter handlers
  const handleCountrySelect = (country) => {
    if (!country) return;

    const countryCode =
      country.alpha3code ||
      country.alpha3 ||
      country.code ||
      country.alpha2 ||
      country.iso2 ||
      country.code3;

    const normalizedCode = countryCode ? String(countryCode).toUpperCase() : "";

    const newFilters = {
      ...filters,
      country: normalizedCode,
    };

    setFilters(newFilters);
    setCountrySearch(country.name || "");
    setShowCountryDropdown(false);

    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, newFilters);
  };

  const handleDivisionCountrySelect = (country) => {
    if (!country) return;

    const countryCode =
      country.alpha3code ||
      country.alpha3 ||
      country.code ||
      country.alpha2 ||
      country.iso2 ||
      country.code3;

    const normalizedCode = countryCode ? String(countryCode).toUpperCase() : "";

    const newFilters = {
      ...filters,
      divisionCountry: normalizedCode,
      competition_id: "",
    };

    setFilters(newFilters);
    setDivisionCountrySearch(country.name || "");
    setCompetitionSearch("");
    setCompetitions([]);
    setFilteredCompetitions([]);
    setShowDivisionCountryDropdown(false);

    // Do NOT fetch transfers - only load leagues
  };

  const handleCompetitionSelect = (competition) => {
    const newFilters = { ...filters, competition_id: competition.id };
    setFilters(newFilters);
    setCompetitionSearch(competition.name || competition.title);
    setShowCompetitionDropdown(false);

    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, newFilters);
  };

  const handleLevelChange = (value) => {
    const newFilters = { ...filters, level: value };
    setFilters(newFilters);
    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, newFilters);
  };

  const handlePositionChange = (value) => {
    const newFilters = { ...filters, position: value };
    setFilters(newFilters);
    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, newFilters);
  };

  const handleTypeChange = (value) => {
    const newFilters = { ...filters, type: value };
    setFilters(newFilters);
    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, newFilters);
  };

  const handleDateFromChange = (value) => {
    const newFilters = { ...filters, date_from: value };
    setFilters(newFilters);
    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, newFilters);
  };

  const handleDateToChange = (value) => {
    const newFilters = { ...filters, date_to: value };
    setFilters(newFilters);
    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, newFilters);
  };

  const handleClearAllFilters = () => {
    const clearedFilters = {
      competition_id: "",
      position: "",
      gender: "male",
      level: "pro",
      country: "",
      divisionCountry: "",
      type: "",
      date_from: "",
      date_to: "",
    };

    setFilters(clearedFilters);
    setCompetitionSearch("");
    setCountrySearch("");
    setDivisionCountrySearch("");
    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, clearedFilters);
  };

  const handleClearFilter = (filterKey) => {
    const newFilters = { ...filters, [filterKey]: "" };
    setFilters(newFilters);

    if (filterKey === "country") {
      setCountrySearch("");
    } else if (filterKey === "divisionCountry") {
      setDivisionCountrySearch("");
      setCompetitions([]);
      setFilteredCompetitions([]);
      newFilters.competition_id = "";
      setCompetitionSearch("");
    } else if (filterKey === "competition_id") {
      setCompetitionSearch("");
    }

    setCursorStack([null]);
    setNextCursor(null);
    setNextPageNumber(null);
    setIsUsingCursorPagination(false);
    setCurrentPage(1);
    fetchTransfers(null, newFilters);
  };

  const getSelectedCountryName = () => {
    if (!filters.country || !countries) return "";
    const selected = countries.find((c) => {
      const code =
        c?.alpha3code ||
        c?.alpha3 ||
        c?.code ||
        c?.alpha2 ||
        c?.iso2 ||
        c?.code3;
      return code && String(code).toUpperCase() === filters.country;
    });
    return selected?.name || "";
  };

  const getSelectedDivisionCountryName = () => {
    if (!filters.divisionCountry || !countries) return "";
    const selected = countries.find((c) => {
      const code =
        c?.alpha3code ||
        c?.alpha3 ||
        c?.code ||
        c?.alpha2 ||
        c?.iso2 ||
        c?.code3;
      return code && String(code).toUpperCase() === filters.divisionCountry;
    });
    return selected?.name || "";
  };

  const getSelectedCompetitionName = () => {
    if (!filters.competition_id) return "";
    const selected = competitions.find(
      (c) => String(c.id) === String(filters.competition_id)
    );
    return selected?.name || selected?.title || "";
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === "gender" && value === "male") return false;
    if (key === "level" && value === "pro") return false;
    return value !== "" && value !== null && value !== undefined;
  });

  // Pagination handlers
  const handlePrevPage = async () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      // If using cursor-based pagination, use cursor stack
      if (cursorStack.length > 1) {
        const newStack = cursorStack.slice(0, -1);
        const prevCursor = newStack[newStack.length - 1];
        setCursorStack(newStack);
        await fetchTransfers(prevCursor || null);
      } else {
        // Use page-based pagination - pass the new page number
        await fetchTransfers(null, null, newPage);
      }
    }
  };

  const handleNextPage = async () => {
    if (hasMore) {
      if (isUsingCursorPagination && nextCursor) {
        // Using cursor-based pagination
        const newPage = currentPage + 1;
        setCurrentPage(newPage);
        setCursorStack((prev) => [...prev, nextCursor]);
        await fetchTransfers(nextCursor);
      } else if (nextPageNumber) {
        // Using page-based pagination
        setCurrentPage(nextPageNumber);
        await fetchTransfers(null, null, nextPageNumber);
      }
    }
  };

  // Abort on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchTransfers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    transfers,
    isLoadingTransfers,
    transfersError,
    currentPage,
    hasMore,
    filters,
    competitionSearch,
    setCompetitionSearch,
    showCompetitionDropdown,
    setShowCompetitionDropdown,
    countrySearch,
    setCountrySearch,
    showCountryDropdown,
    setShowCountryDropdown,
    divisionCountrySearch,
    setDivisionCountrySearch,
    showDivisionCountryDropdown,
    setShowDivisionCountryDropdown,
    filteredCompetitions,
    filteredCountries,
    filteredDivisionCountries,
    hasActiveFilters,
    handleCountrySelect,
    handleDivisionCountrySelect,
    handleCompetitionSelect,
    handleLevelChange,
    handlePositionChange,
    handleTypeChange,
    handleDateFromChange,
    handleDateToChange,
    handleClearAllFilters,
    handleClearFilter,
    getSelectedCountryName,
    getSelectedDivisionCountryName,
    getSelectedCompetitionName,
    handlePrevPage,
    handleNextPage,
    fetchTransfers,
  };
};

export default useTransferSection;
